/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/components/AuthProvider';
import { useTheme } from '@/App';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'motion/react';
import {
  Briefcase,
  Calendar,
  Plus,
  PlusCircle,
  Check,
  Upload,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  X,
  FileStack,
  Clock,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import { differenceInDays, parseISO, format, addDays } from 'date-fns';

// --- Types ---

interface Booking {
  id: string;
  company: string;
  role?: string;
  startDate: string;
  endDate: string;
  rate: number;
  rateType: 'daily' | 'hourly';
  hoursPerDay?: number;
  days: number;
  earnings: number;
  status: 'Active' | 'Pending' | 'Paid';
  currency: string;
  importSource?: 'csv' | 'manual';
  reasoning?: string;
}

interface YearlyPerformance {
  year: string;
  totalEarned: number;
  totalDaysWorked: number;
  avgDayRate: number;
  reasoning?: string;
}

type ImportStep = 'CSV_IMPORT' | 'FIRST_BOOKING' | 'ADD_MORE_LOOP' | 'PAST_EARNINGS';

// --- Helpers ---

const formatCurrency = (amount: number, currency = 'USD'): string => {
  const safeAmount = isNaN(amount) || amount == null ? 0 : amount;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(safeAmount);
};

const formatDateRange = (start: string, end: string) => {
  if (!start || !end) return '';
  try {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    return `${format(startDate, 'MMM dd')} – ${format(endDate, 'MMM dd, yyyy')}`;
  } catch (e) {
    return '';
  }
};

// --- Component ---

export default function ImportPage() {
  const { user } = useAuth();
  const { currency: userCurrency } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const initialStep: ImportStep =
    searchParams.get('step') === 'past_earnings' ? 'PAST_EARNINGS' : 'CSV_IMPORT';
  const [step, setStep] = useState<ImportStep>(initialStep);
  const [returnStep, setReturnStep] = useState<ImportStep | null>(null);

  // CSV Import State
  const [csvContent, setCsvContent] = useState({
    currentYear: false,
    previousYear: false,
    multipleYears: false,
    pastSummary: false
  });
  const [importSuccess, setImportSuccess] = useState<{ count: number; type: 'bookings' | 'years' } | null>(null);
  const [pendingImportedBookings, setPendingImportedBookings] = useState<Booking[]>([]);
  const [totalImportedBookings, setTotalImportedBookings] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [csvImportMode, setCsvImportMode] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);
  const [importDatesNeedYear, setImportDatesNeedYear] = useState(false);
  const [importYearBannerValue, setImportYearBannerValue] = useState(new Date().getFullYear().toString());

  // Bookings State
  const [bookedJobs, setBookedJobs] = useState<Booking[]>([]);
  const [currentJob, setCurrentJob] = useState({
    company: '',
    startDate: '',
    endDate: '',
    rate: '',
    rateType: 'daily' as 'daily' | 'hourly',
    hoursPerDay: '8',
    currency: userCurrency || 'USD'
  });
  const [manualBilled, setManualBilled] = useState<string | null>(null);
  const [importedDays, setImportedDays] = useState<number | null>(null);
  const [importedEarnings, setImportedEarnings] = useState<number | null>(null);
  const [hasManuallyEditedBilled, setHasManuallyEditedBilled] = useState(false);
  const [bookingYear, setBookingYear] = useState(new Date().getFullYear().toString());
  const [isEarningsFocused, setIsEarningsFocused] = useState(false);

  // Historical State
  const [performanceHistory, setPerformanceHistory] = useState<YearlyPerformance[]>([]);
  const [confirmArchiveIdx, setConfirmArchiveIdx] = useState<number | null>(null);

  // Help modal
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Parsing State
  const [isParsing, setIsParsing] = useState(false);
  const [videoIndex] = useState(0);
  const loadingVideos = [
    "https://res.cloudinary.com/dlmbskxuc/video/upload/v1776708138/loading_firsthold2_srwg3e.mp4",
    "https://res.cloudinary.com/dlmbskxuc/video/upload/v1776710228/firsthold_car_loader2_wuwmvf.mp4"
  ];
  const [isTakingLonger, setIsTakingLonger] = useState(false);
  const [isSweating, setIsSweating] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Saving State
  const [isSaving, setIsSaving] = useState(false);

  // Manual entry choice modal
  const [showManualEntryChoice, setShowManualEntryChoice] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Default rate type for Gemini parsing
  const defaultRateType: 'daily' | 'hourly' = 'daily';

  // On mount: build performanceHistory by merging yearly_performance (snapshot)
  // with live totals calculated from the bookings table.
  // Bookings-derived values always win — they reflect every booking ever added,
  // not just what was imported when yearly_performance was last written.
  useEffect(() => {
    if (!user) return;

    Promise.all([
      supabase
        .from('yearly_performance')
        .select('year, total_earned, total_days_worked, avg_day_rate')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('year', { ascending: false }),
      supabase
        .from('bookings')
        .select('start_date, earnings, days')
        .eq('user_id', user.id)
        .eq('archived', false),
    ]).then(([perfRes, bookRes]) => {
      // Start from yearly_performance snapshot
      const historyMap: Record<string, YearlyPerformance> = {};
      (perfRes.data ?? []).forEach((r: any) => {
        const yr = String(r.year);
        historyMap[yr] = {
          year: yr,
          totalEarned: r.total_earned ?? 0,
          totalDaysWorked: r.total_days_worked ?? 0,
          avgDayRate: r.avg_day_rate ?? 0,
        };
      });

      // Recalculate per-year totals from actual bookings and override snapshot
      const byYear: Record<string, { earned: number; days: number }> = {};
      (bookRes.data ?? []).forEach((b: any) => {
        const yr = b.start_date?.split('-')[0];
        if (!yr) return;
        if (!byYear[yr]) byYear[yr] = { earned: 0, days: 0 };
        byYear[yr].earned += isNaN(b.earnings) ? 0 : (b.earnings ?? 0);
        byYear[yr].days  += b.days ?? 0;
      });

      Object.entries(byYear).forEach(([yr, { earned, days }]) => {
        historyMap[yr] = {
          year: yr,
          totalEarned: earned,
          totalDaysWorked: days,
          avgDayRate: days > 0 ? earned / days : 0,
        };
      });

      const sorted = Object.values(historyMap).sort(
        (a, b) => parseInt(b.year) - parseInt(a.year)
      );
      if (sorted.length > 0) setPerformanceHistory(sorted);
    });
  }, [user]);

  // --- Calculations ---

  const currentJobStats = useMemo(() => {
    if (!currentJob.startDate || !currentJob.endDate) return { days: 0, billed: 0 };
    const start = parseISO(currentJob.startDate);
    const end = parseISO(currentJob.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return { days: 0, billed: 0 };
    const days = Math.max(0, differenceInDays(end, start) + 1);
    const rate = parseFloat(currentJob.rate || '0');
    let billed = 0;
    if (currentJob.rateType === 'daily') {
      billed = days * rate;
    } else {
      billed = days * parseFloat(currentJob.hoursPerDay) * rate;
    }
    return { days, billed: manualBilled !== null ? parseFloat(manualBilled) : billed };
  }, [currentJob, manualBilled]);

  const totalProjectedYTD = useMemo(() => {
    return bookedJobs.reduce((acc, job) => {
      const safeEarnings = isNaN(job.earnings) || job.earnings == null ? 0 : job.earnings;
      return acc + safeEarnings;
    }, 0);
  }, [bookedJobs]);

  useEffect(() => {
    if (step === 'PAST_EARNINGS' && bookedJobs.length > 0) {
      const groupedByYear: Record<string, { earned: number; days: number }> = {};
      bookedJobs.forEach(job => {
        if (!job.company || job.earnings <= 0) return;
        const year = job.startDate?.split('-')[0];
        if (!year) return;
        if (!groupedByYear[year]) groupedByYear[year] = { earned: 0, days: 0 };
        groupedByYear[year].earned += job.earnings || 0;
        groupedByYear[year].days += job.days || 0;
      });
      setPerformanceHistory(prev => {
        const newHistory = [...prev];
        Object.entries(groupedByYear).forEach(([year, data]) => {
          const existingIdx = newHistory.findIndex(h => h.year === year);
          if (existingIdx !== -1) {
            const existing = newHistory[existingIdx];
            if (existing.totalEarned !== data.earned || existing.totalDaysWorked !== data.days) {
              newHistory[existingIdx] = {
                year,
                totalEarned: data.earned,
                totalDaysWorked: data.days,
                avgDayRate: data.days > 0 ? data.earned / data.days : 0
              };
            }
          } else {
            newHistory.push({
              year,
              totalEarned: data.earned,
              totalDaysWorked: data.days,
              avgDayRate: data.days > 0 ? data.earned / data.days : 0
            });
          }
        });
        const hasChanged = JSON.stringify(newHistory) !== JSON.stringify(prev);
        return hasChanged ? newHistory.sort((a, b) => parseInt(b.year) - parseInt(a.year)) : prev;
      });
    }
  }, [step, bookedJobs]);

  const calculateDaysFromDates = (startStr: string, endStr: string) => {
    if (!startStr || !endStr) return 0;
    try {
      const start = parseISO(startStr);
      const end = parseISO(endStr);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
      return Math.max(0, differenceInDays(end, start) + 1);
    } catch {
      return 0;
    }
  };

  const syncBookingCalculations = (
    field: 'rate' | 'days' | 'earnings' | 'dates',
    overrides: { rate?: string; days?: number; earnings?: number; start?: string; end?: string } = {}
  ) => {
    const rateS = overrides.rate !== undefined ? overrides.rate : currentJob.rate;
    const daysV = overrides.days !== undefined ? overrides.days : (importedDays ?? 0);
    const earningsV = overrides.earnings !== undefined ? overrides.earnings : (importedEarnings ?? 0);
    const startS = overrides.start !== undefined ? overrides.start : currentJob.startDate;
    const endS = overrides.end !== undefined ? overrides.end : currentJob.endDate;
    const rateNum = parseFloat(rateS || '0');
    const hpdNum = parseFloat(currentJob.hoursPerDay || '8');
    const isDaily = currentJob.rateType === 'daily';
    if (field === 'dates') {
      const newDays = calculateDaysFromDates(startS, endS);
      setImportedDays(newDays);
      if (!hasManuallyEditedBilled && rateNum > 0) {
        setImportedEarnings(isDaily ? newDays * rateNum : newDays * hpdNum * rateNum);
      }
    } else if (field === 'rate') {
      if (!hasManuallyEditedBilled && (overrides.days !== undefined || (importedDays !== null && importedDays !== 0))) {
        const effectiveDays = overrides.days !== undefined ? overrides.days : (importedDays || 0);
        setImportedEarnings(isDaily ? effectiveDays * rateNum : effectiveDays * hpdNum * rateNum);
      }
    } else if (field === 'days') {
      if (!hasManuallyEditedBilled && rateNum > 0) {
        setImportedEarnings(isDaily ? daysV * rateNum : daysV * hpdNum * rateNum);
      }
    } else if (field === 'earnings') {
      if (rateNum > 0 && daysV === 0) {
        const calcDays = isDaily ? earningsV / rateNum : earningsV / (hpdNum * rateNum);
        if (calcDays >= 0.5) setImportedDays(calcDays);
      }
    }
  };

  // --- Handlers ---

  const handleAddBooking = () => {
    const newBooking: Booking = {
      id: Date.now().toString(),
      company: currentJob.company,
      startDate: currentJob.startDate,
      endDate: currentJob.endDate,
      rate: parseFloat(currentJob.rate) || 0,
      rateType: currentJob.rateType,
      hoursPerDay: currentJob.rateType === 'hourly' ? (parseFloat(currentJob.hoursPerDay) || 8) : undefined,
      days: importedDays ?? currentJobStats.days,
      earnings: importedEarnings ?? currentJobStats.billed,
      status: 'Active',
      currency: currentJob.currency,
      importSource: pendingImportedBookings.length > 0 ? 'csv' : 'manual',
    };
    setBookedJobs(prev => [...prev, newBooking]);

    if (pendingImportedBookings.length > 0) {
      setPendingImportedBookings(prevPending => {
        const remaining = prevPending.slice(1);
        if (remaining.length > 0) {
          const next = remaining[0];
          setCurrentJob({
            company: next.company,
            startDate: next.startDate,
            endDate: next.endDate,
            rate: next.rate === 0 ? '' : next.rate.toString(),
            rateType: next.rateType,
            hoursPerDay: '8',
            currency: next.currency
          });
          setImportedDays(next.days);
          setImportedEarnings(next.earnings);
          setHasManuallyEditedBilled(false);
          setBookingYear(new Date().getFullYear().toString());
        } else {
          setCurrentJob({ company: '', startDate: '', endDate: '', rate: '', rateType: 'daily', hoursPerDay: '8', currency: userCurrency || 'USD' });
          setManualBilled(null);
          setImportedDays(null);
          setImportedEarnings(null);
          setHasManuallyEditedBilled(false);
          setBookingYear(new Date().getFullYear().toString());
          if (returnStep) {
            setStep(returnStep);
            setReturnStep(null);
          } else {
            setStep('ADD_MORE_LOOP');
          }
          setCsvImportMode(false);
          setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
          setImportSuccess(null);
          setImportError(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
        return remaining;
      });
      return;
    }

    setCurrentJob({ company: '', startDate: '', endDate: '', rate: '', rateType: 'daily', hoursPerDay: '8', currency: userCurrency || 'USD' });
    setManualBilled(null);
    setImportedDays(null);
    setImportedEarnings(null);
    setHasManuallyEditedBilled(false);
    setBookingYear(new Date().getFullYear().toString());

    if (returnStep && returnStep !== 'PAST_EARNINGS') {
      setStep(returnStep);
      setReturnStep(null);
    } else {
      setStep('ADD_MORE_LOOP');
    }
  };

  const handleBulkConfirm = () => {
    const bulkBookings = pendingImportedBookings.map(b => ({
      ...b,
      earnings: isNaN(b.earnings) || b.earnings == null ? 0 : b.earnings
    }));
    setBookedJobs([...bookedJobs, ...bulkBookings]);
    setPendingImportedBookings([]);
    setShowBulkConfirm(false);
    setCsvImportMode(false);
    setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
    setImportSuccess(null);
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (returnStep) {
      setStep(returnStep);
      setReturnStep(null);
    } else {
      setStep('PAST_EARNINGS');
    }
  };

  const handleUpdateImportYear = (newYear: string) => {
    if (newYear.length !== 4 || isNaN(parseInt(newYear))) return;
    const updateDate = (d: string) => d ? d.replace(/^\d{4}/, newYear) : d;
    setPendingImportedBookings(prev => prev.map(b => ({
      ...b,
      startDate: updateDate(b.startDate),
      endDate: updateDate(b.endDate),
    })));
    setCurrentJob(prev => ({
      ...prev,
      startDate: updateDate(prev.startDate),
      endDate: updateDate(prev.endDate),
    }));
    setImportDatesNeedYear(false);
  };

  const handleAddDetailedBooking = (year: string) => {
    setReturnStep('PAST_EARNINGS');
    setBookingYear(year);
    setCurrentJob({
      company: '',
      startDate: `${year}-01-01`,
      endDate: `${year}-01-01`,
      rate: '',
      rateType: 'daily',
      hoursPerDay: '8',
      currency: currentJob.currency
    });
    setImportedDays(null);
    setImportedEarnings(null);
    setHasManuallyEditedBilled(false);
    setStep('FIRST_BOOKING');
    setShowManualEntryChoice(false);
  };

  const handleAddYear = () => {
    const lastYear = performanceHistory.length > 0
      ? (parseInt(performanceHistory[performanceHistory.length - 1].year) - 1).toString()
      : (new Date().getFullYear() - 1).toString();
    setPerformanceHistory([...performanceHistory, { year: lastYear, totalEarned: 0, totalDaysWorked: 0, avgDayRate: 0 }]);
  };

  const updateYearData = (index: number, field: keyof YearlyPerformance, value: string | number) => {
    const updated = [...performanceHistory];
    let processedValue = value;
    if (field === 'totalEarned' && typeof value === 'string') {
      processedValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
    }
    updated[index] = { ...updated[index], [field]: processedValue };
    if (field === 'totalEarned' || field === 'totalDaysWorked') {
      const earned = field === 'totalEarned' ? Number(processedValue) : updated[index].totalEarned;
      const days = field === 'totalDaysWorked' ? Number(processedValue) : updated[index].totalDaysWorked;
      updated[index].avgDayRate = days > 0 ? earned / days : 0;
    }
    if (field === 'year') {
      updated.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    }
    setPerformanceHistory(updated);
  };

  // Thin client wrapper — actual Gemini logic lives in /api/parse-csv (server-side).
  const parseFileWithGemini = async (
    checkedLabels: string,
    fileData: string,
    isHistoricalView: boolean,
    userDefaultRateType: 'daily' | 'hourly',
    userDefaultCurrency: string = 'USD',
    mimeType: string = 'text/plain',
    fileName = '',
    fileType = '',
  ) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch('/api/parse-csv', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        fileData,
        mimeType,
        checkedLabels,
        isHistorical: isHistoricalView,
        defaultRateType: userDefaultRateType,
        defaultCurrency: userDefaultCurrency,
        fileName,
        fileType,
      }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Parsing failed on server.');
    return json;
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>, isHistorical: boolean = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileNameYearMatch = file.name.match(/\b(20\d{2})\b/);
    const fileNameYear: number | null = fileNameYearMatch ? parseInt(fileNameYearMatch[1]) : null;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isExcel = !isPdf && (file.type.includes('spreadsheet') || file.type.includes('excel') ||
      file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls'));

    setIsParsing(true);
    setIsTakingLonger(false);
    setIsSweating(false);
    setElapsedSeconds(0);
    setImportError(null);
    setImportSuccess(null);
    setWarnings([]);
    setCsvImportMode(true);

    const effectiveIsHistorical = isHistorical || returnStep === 'PAST_EARNINGS';

    const counterTimer = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    const takingLongerTimer = setTimeout(() => setIsTakingLonger(true), 30000);
    const sweatingTimer = setTimeout(() => setIsSweating(true), 50000);

    const checkedLabels = isHistorical ? "Historical Yearly Totals" : [
      csvContent.currentYear && "This Year's Bookings",
      csvContent.previousYear && "Last Year's Bookings",
      csvContent.multipleYears && "Other Years",
      csvContent.pastSummary && "Annual Sums Only"
    ].filter(Boolean).join(", ");

    try {
      let fileData = "";
      let mimeType = "text/plain";

      if (isPdf) {
        mimeType = "application/pdf";
        fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } else if (isExcel) {
        mimeType = file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } else {
        const fileText = await file.text();
        const preprocessCsv = (text: string): string => {
          return text.split(/\r?\n/).filter(row => {
            const cleaned = row.replace(/,/g, '').trim();
            return cleaned.length > 0;
          }).join('\n');
        };
        fileData = preprocessCsv(fileText);
      }

      const detectedCurrency = !isPdf && !isExcel && (fileData.includes('£') || /\bGBP\b/i.test(fileData) || /\bpounds?\b/i.test(fileData)) ? 'GBP'
        : !isPdf && !isExcel && (fileData.includes('€') || /\bEUR\b/i.test(fileData)) ? 'EUR'
        : (userCurrency || 'USD');

      // For Excel, fileData is base64 — we can't run regex on it to check for year dates.
      // Don't assume Excel has full-year dates; let the filename year override apply if needed.
      // PDF and Excel send binary/base64 — can't regex-check for year dates client-side.
      // Never assume they have full-year dates; let the filename year override apply.
      const csvHasFullYearDates = !isPdf && !isExcel && (/20\d{2}-\d{1,2}-\d{1,2}/.test(fileData) || /\b(0?[1-9]|1[0-2])[\/](0?[1-9]|[12]\d|3[01])[\/](20\d{2})\b/.test(fileData));
      const shouldOverrideYear = fileNameYear !== null && !csvHasFullYearDates;

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("TIMEOUT")), 120000)
      );

      const result = await Promise.race([
        parseFileWithGemini(checkedLabels, fileData, effectiveIsHistorical, defaultRateType, currentJob.currency || 'USD', mimeType, file.name, file.type),
        timeoutPromise
      ]) as any[];

      clearInterval(counterTimer);
      clearTimeout(takingLongerTimer);
      clearTimeout(sweatingTimer);
      setIsTakingLonger(false);
      setIsSweating(false);

      if (!Array.isArray(result) || result.length === 0) {
        setImportError("No records found. Your file may have an unusual format — try a simpler export.");
        setIsParsing(false);
        return;
      }

      const currentYearNum = new Date().getFullYear();
      const prevYearNum = currentYearNum - 1;

      const historyRecords = result.filter((item: any) =>
        item.type === 'summary' || (!item.type && item.year && (item.totalEarned || item.totalDaysWorked))
      );

      const bookingRecords = result.filter((item: any) => {
        const hasClient = item.clientName && typeof item.clientName === 'string' && item.clientName.trim().length > 0;
        return (item.type === 'booking' || (!item.type && (item.rate || item.totalBilled))) && hasClient;
      });

      // Process History
      let history: YearlyPerformance[] = historyRecords.map((s: any) => {
        const clean = (val: any) => {
          if (typeof val === 'number') return val;
          return parseFloat(String(val || '').replace(/[^0-9.]/g, '')) || 0;
        };
        const earned = clean(s.totalEarned || s.totalBilled);
        const worked = clean(s.totalDaysWorked || s.totalDays);
        let yearVal = s.year?.toString();
        if (!yearVal || yearVal.length < 4) {
          if (csvContent.previousYear) yearVal = prevYearNum.toString();
          else if (csvContent.currentYear) yearVal = currentYearNum.toString();
          else yearVal = currentYearNum.toString();
        }
        return {
          year: yearVal,
          totalEarned: earned,
          totalDaysWorked: worked,
          avgDayRate: worked > 0 ? earned / worked : 0,
          reasoning: s.reasoning
        };
      });

      if (csvContent.currentYear || csvContent.previousYear) {
        const targetYear = csvContent.currentYear ? currentYearNum.toString() : prevYearNum.toString();
        history = history.filter(h => h.year === targetYear || (Math.abs(h.totalEarned) > 0));
        if (history.length > 0) {
          history = history.map(h => ({ ...h, year: targetYear }));
        }
      }

      if ((isHistorical === true || csvContent.pastSummary) && history.length > 0) {
        setPerformanceHistory(prev => {
          const uniqueHistory = history.filter((h, i, self) =>
            i === self.findIndex(t => t.year === h.year || (Math.abs(t.totalEarned - h.totalEarned) < 1 && t.totalDaysWorked === h.totalDaysWorked && h.totalEarned > 0))
          );
          const newHistory = [...prev];
          uniqueHistory.forEach(newYear => {
            const existingIdx = newHistory.findIndex(h => h.year === newYear.year);
            if (existingIdx !== -1) {
              newHistory[existingIdx] = newYear;
            } else {
              newHistory.push(newYear);
            }
          });
          return newHistory.sort((a, b) => parseInt(b.year) - parseInt(a.year));
        });
      }

      // Process Bookings
      // For "Other Years" imports, infer the dominant year from what the AI parsed.
      // The API prompt now instructs Gemini to extract the year from document context,
      // so AI-returned dates should already carry the correct 4-digit year.
      let multipleYearsDefaultYear = currentYearNum;
      if (csvContent.multipleYears) {
        const yearCounts: Record<number, number> = {};
        for (const b of bookingRecords) {
          for (const dateField of [b.startDate, b.endDate]) {
            const match = String(dateField || '').match(/\b(20\d{2})\b/);
            if (match) {
              const y = parseInt(match[1]);
              yearCounts[y] = (yearCounts[y] || 0) + 1;
            }
          }
        }
        const entries = Object.entries(yearCounts);
        if (entries.length > 0) {
          multipleYearsDefaultYear = parseInt(entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0]);
        }
      }

      const rawBookings: Booking[] = bookingRecords.map((b: any) => {
        const cleanNum = (val: any) => {
          if (typeof val === 'number') return val;
          return parseFloat(String(val || '').replace(/[^0-9.]/g, '')) || 0;
        };
        const detectCurrency = (val: any, existing: string) => {
          const s = String(val || '').toUpperCase();
          if (s.includes('£') || s.includes('GBP') || s.includes('POUND')) return 'GBP';
          if (s.includes('€') || s.includes('EUR')) return 'EUR';
          if (s.includes('$') || s.includes('USD')) return 'USD';
          return existing || 'USD';
        };

        let rate = cleanNum(b.rate);
        let earnings = cleanNum(b.totalBilled) || cleanNum(b.totalEarned);
        let days = parseFloat(String(b.totalDays || b.totalDaysWorked || '').replace(/[^0-9.]/g, '')) || 0;

        const defaultYear = fileNameYear ?? (csvContent.previousYear ? prevYearNum : (csvContent.multipleYears ? multipleYearsDefaultYear : currentYearNum));
        const sanitizeDate = (dateStr: string) => {
          if (!dateStr || typeof dateStr !== 'string' || dateStr.trim() === '') return `${defaultYear}-01-01`;
          const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
          if (isoMatch) return shouldOverrideYear ? `${fileNameYear}-${isoMatch[2].padStart(2,'0')}-${isoMatch[3].padStart(2,'0')}` : dateStr;
          const parts = dateStr.split(/[\/\-\.]/).map(p => p.trim());
          if (parts.length >= 3) {
            let yearIdx = parts.findIndex(p => p.length === 4);
            let year = yearIdx !== -1 ? parts[yearIdx] : '';
            if (!year) {
              if (parts[2].length === 2) { year = '20' + parts[2]; yearIdx = 2; }
              else { year = defaultYear.toString(); }
            }
            if (shouldOverrideYear && fileNameYear !== null) year = fileNameYear.toString();
            const others = parts.filter((_, i) => i !== yearIdx);
            let month = '01', day = '01';
            if (yearIdx === 0) { month = others[0] || '01'; day = others[1] || '01'; }
            else { month = others[0] || '01'; day = others[1] || '01'; }
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
          if (parts.length === 2) {
            return `${defaultYear}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
          return `${defaultYear}-01-01`;
        };

        const sDate = sanitizeDate(b.startDate);
        const eDate = b.endDate ? sanitizeDate(b.endDate) : (b.startDate ? sDate : sanitizeDate(''));

        if (days === 0 && sDate && eDate) {
          const start = parseISO(sDate);
          const end = parseISO(eDate);
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            days = Math.max(0, differenceInDays(end, start) + 1);
          }
        }

        if (rate === 0 && earnings > 0 && days > 0) rate = earnings / days;
        else if (earnings === 0 && rate > 0 && days > 0) earnings = rate * days;

        let currency = detectCurrency(b.currency, '');
        if (currency === 'USD') currency = detectCurrency(b.totalBilled || b.totalEarned, 'USD');
        if (currency === 'USD') currency = detectCurrency(b.rate, 'USD');

        return {
          id: (Date.now() + Math.random()).toString(),
          company: b.clientName || '',
          startDate: sDate,
          endDate: eDate,
          rate,
          rateType: (b.rateType || (String(b.rate).toLowerCase().includes('hour') ? 'hourly' : 'daily')) as 'daily' | 'hourly',
          days: days || 0,
          earnings: earnings || 0,
          status: 'Active' as const,
          currency: currency !== 'USD' ? currency : detectedCurrency,
          importSource: 'csv' as const,
          reasoning: b.reasoning
        };
      });

      const bookings = rawBookings.filter((b, index, self) =>
        index === self.findIndex(t => (
          t.company === b.company &&
          t.startDate === b.startDate &&
          t.endDate === b.endDate &&
          Math.abs(t.earnings - b.earnings) < 1
        ))
      );

      if (bookings.length > 0) {
        setPendingImportedBookings(bookings);
        setTotalImportedBookings(bookings.length);
        const first = bookings[0];
        setImportedDays(first.days);
        setImportedEarnings(first.earnings);
        setHasManuallyEditedBilled(false);
        setCurrentJob({
          company: first.company,
          startDate: first.startDate,
          endDate: first.endDate,
          rate: first.rate === 0 ? '' : first.rate.toString(),
          rateType: first.rateType,
          hoursPerDay: '8',
          currency: first.currency || 'USD'
        });
        const needsYearInput = fileNameYear === null && (!csvHasFullYearDates || csvContent.multipleYears);
        const inferredYear = bookings[0]?.startDate?.split('-')[0] || new Date().getFullYear().toString();
        setImportDatesNeedYear(needsYearInput);
        setImportYearBannerValue(inferredYear);
        setImportSuccess({ count: bookings.length, type: 'bookings' });
        setTimeout(() => {
          setStep('FIRST_BOOKING');
          setIsParsing(false);
        }, 1500);
      } else if (history.length > 0 && (isHistorical === true || csvContent.pastSummary || csvContent.multipleYears)) {
        setImportSuccess({ count: history.length, type: 'years' });
        setTimeout(() => {
          if (returnStep) {
            setStep(returnStep);
            setReturnStep(null);
          } else {
            setStep('PAST_EARNINGS');
          }
          setIsParsing(false);
        }, 1500);
      } else {
        setImportError("No individual booking records found. Verify you selected the correct file content type.");
        setIsParsing(false);
      }
      setIsParsing(false);
    } catch (error: any) {
      clearInterval(counterTimer);
      clearTimeout(takingLongerTimer);
      clearTimeout(sweatingTimer);
      setIsTakingLonger(false);
      setIsSweating(false);
      setIsParsing(false);
      if (error.message === "TIMEOUT") {
        setImportError("Request timed out (120s). Please try again.");
      } else {
        setImportError(error.message || "Could not parse file. Please try again or enter data manually.");
      }
    }
  };

  // --- Save & Close handler ---

  const handleSaveAndClose = async () => {
    if (!user) return;
    setIsSaving(true);

    try {
      // Unarchive yearly_performance rows for re-imported years so the year
      // reappears in stats. Archived bookings are left archived — re-import
      // always inserts fresh rows, never restores old archived data.
      const importedYears = Array.from(new Set(
        bookedJobs.map(j => j.startDate?.split('-')[0]).filter(Boolean)
      ));
      for (const year of importedYears) {
        await supabase.from('yearly_performance')
          .update({ archived: false })
          .eq('user_id', user.id)
          .eq('year', parseInt(year))
          .eq('archived', true);
      }

      // Fetch active (non-archived) bookings for dedup — archived rows are ignored
      const { data: existingBookings } = await supabase
        .from('bookings')
        .select('id, company, start_date, archived')
        .eq('user_id', user.id);

      const activeSet = new Set(
        (existingBookings || []).filter((b: any) => !b.archived).map((b: any) => `${b.company}::${b.start_date}`)
      );

      // Bookings that exist as active → skip (true duplicates, already unarchived above)
      // Bookings that don't exist at all → insert fresh
      const toInsert = bookedJobs.filter(job => !activeSet.has(`${job.company}::${job.startDate}`));
      const duplicateCount = bookedJobs.length - toInsert.length;

      // Insert truly new bookings
      if (toInsert.length > 0) {
        const { error: bookErr } = await supabase.from('bookings').insert(
          toInsert.map(job => ({
            user_id: user.id,
            company: job.company,
            role: job.role || '',
            start_date: job.startDate,
            end_date: job.endDate,
            days: job.days,
            rate: job.rate,
            rate_type: job.rateType,
            hours_per_day: job.hoursPerDay ?? 8,
            earnings: isNaN(job.earnings) ? 0 : job.earnings,
            status: job.status,
            currency: job.currency || 'USD',
            import_source: job.importSource || 'manual',
            archived: false,
          }))
        );
        if (bookErr) {
          console.error('[ImportPage] bookings insert error:', bookErr);
          throw new Error(`Booking insert failed: ${bookErr.message}`);
        }
      }

      const newBookings = [...toInsert];

      // Recalculate yearly totals directly from bookedJobs so the upsert always
      // reflects the current import, never the stale on-mount DB snapshot.
      const freshByYear: Record<string, { earned: number; days: number }> = {};
      bookedJobs.forEach(job => {
        const yr = job.startDate?.split('-')[0];
        if (!yr || !job.company || isNaN(job.earnings)) return;
        if (!freshByYear[yr]) freshByYear[yr] = { earned: 0, days: 0 };
        freshByYear[yr].earned += job.earnings || 0;
        freshByYear[yr].days += job.days || 0;
      });

      // Upsert yearly_performance — merge fresh bookedJobs totals into performanceHistory
      if (performanceHistory.length > 0) {
        const perfRows = performanceHistory.map(p => {
          const fresh = freshByYear[p.year];
          const earned = fresh ? fresh.earned : p.totalEarned;
          const days = fresh ? fresh.days : p.totalDaysWorked;
          return {
            user_id: user.id,
            year: parseInt(p.year),
            total_earned: earned,
            total_days_worked: days,
            avg_day_rate: days > 0 ? earned / days : 0,
            currency: bookedJobs[0]?.currency || userCurrency || 'USD',
            import_source: 'manual',
            archived: false,
          };
        });
        // Also upsert any imported years not already in performanceHistory
        Object.entries(freshByYear).forEach(([yr, { earned, days }]) => {
          if (!perfRows.find(r => r.year === parseInt(yr))) {
            perfRows.push({
              user_id: user.id,
              year: parseInt(yr),
              total_earned: earned,
              total_days_worked: days,
              avg_day_rate: days > 0 ? earned / days : 0,
              currency: bookedJobs[0]?.currency || userCurrency || 'USD',
              import_source: 'manual',
              archived: false,
            });
          }
        });
        const { error: perfErr } = await supabase
          .from('yearly_performance')
          .upsert(perfRows, { onConflict: 'user_id,year' });
        if (perfErr) console.error('[ImportPage] yearly_performance upsert error:', perfErr);
      }

      // Success toast
      const parts: string[] = [];
      if (newBookings.length > 0) parts.push(`${newBookings.length} booking${newBookings.length !== 1 ? 's' : ''} added`);
      if (duplicateCount > 0) parts.push(`${duplicateCount} duplicate${duplicateCount !== 1 ? 's' : ''} skipped`);
      if (performanceHistory.length > 0 && newBookings.length === 0) parts.push('history updated');
      toast({
        title: 'Import complete',
        description: parts.length > 0 ? parts.join(', ') : 'No new bookings to add.',
      });

      navigate('/settings');
    } catch (err) {
      console.error('[ImportPage] handleSaveAndClose error:', err);
      toast({ title: 'Error', description: 'Could not save data. Please try again.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Style constants ---

  const inputClasses = "w-full bg-white/50 border border-gray-200 rounded-2xl py-3 pl-12 pr-4 text-base font-bold text-[#1A1F36] focus:outline-none focus:border-[#4A6CF7]/40 focus:bg-white/60 transition-all";
  const dateInputClasses = "w-full bg-white/50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-[#1A1F36] focus:outline-none focus:border-[#4A6CF7]/20 focus:bg-white/60 transition-all appearance-none";
  const labelClasses = "block uppercase text-[10px] font-bold tracking-[0.2em] text-[#45556c] mb-1.5 ml-1";
  const backBtnClasses = "px-5 py-3.5 rounded-xl font-black uppercase text-[10px] tracking-[0.2em] text-[#45556c] hover:bg-black/5 transition-all flex items-center gap-2";
  const primaryBtnClasses = "flex-1 bg-black text-white py-3.5 rounded-full font-black uppercase text-[10px] tracking-[0.2em] hover:scale-[1.05] active:scale-[0.95] transition-all shadow-xl disabled:opacity-20";

  const isAnyCsvChecked = Object.values(csvContent).some(v => v);

  // --- Render helpers ---

  const renderCsvImport = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <div className="space-y-4">
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
          <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
            Import your<br />
            <span className="text-[#4A6CF7]">Booking History.</span>
          </h1>
        </div>

        <div className="w-full max-w-md mx-auto space-y-4">
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/50 text-center">Select what your file contains before uploading.</p>

          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'currentYear', label: "This Year's Bookings", hint: `Individual jobs from ${new Date().getFullYear()}` },
              { id: 'previousYear', label: "Last Year's Bookings", hint: `Individual jobs from ${new Date().getFullYear() - 1}` },
              { id: 'multipleYears', label: "Other Years", hint: `${new Date().getFullYear() - 2} or any earlier year` },
              { id: 'pastSummary', label: "Annual Sums Only", hint: "Year totals, no individual jobs" }
            ].map(opt => {
              const checked = csvContent[opt.id as keyof typeof csvContent];
              return (
                <button
                  key={opt.id}
                  onClick={() => {
                    setCsvContent({
                      currentYear: opt.id === 'currentYear',
                      previousYear: opt.id === 'previousYear',
                      multipleYears: opt.id === 'multipleYears',
                      pastSummary: opt.id === 'pastSummary'
                    });
                    setShowCheckboxError(false);
                  }}
                  className={`flex items-center justify-between gap-3 px-4 py-3 border rounded-xl transition-all text-[9px] font-bold uppercase tracking-wider text-left ${checked ? 'bg-[#4A6CF7]/10 border-[#4A6CF7]/30 text-[#4A6CF7]' : (showCheckboxError ? 'bg-red-50 border-red-400 text-black/40' : 'bg-white/60 border-black/10 text-black/40 hover:bg-black/5')}`}
                >
                  <div className="flex flex-col flex-1"><span>{opt.label}</span><span className={`text-[9px] font-normal normal-case tracking-normal mt-0.5 ${checked ? 'opacity-70' : 'opacity-50'}`}>{opt.hint}</span></div>
                  <div className={`shrink-0 w-4 h-4 rounded flex items-center justify-center transition-all ${checked ? 'bg-[#4A6CF7]' : 'border border-[#1A1F36]/30'}`}>
                    {checked && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>

          <div
            onClick={() => {
              if (isAnyCsvChecked) { setShowCheckboxError(false); fileInputRef.current?.click(); }
              else { setShowCheckboxError(true); }
            }}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
            onDrop={e => {
              e.preventDefault();
              setIsDragging(false);
              if (!isAnyCsvChecked) { setShowCheckboxError(true); return; }
              const file = e.dataTransfer.files?.[0];
              if (file) handleCsvUpload({ target: { files: e.dataTransfer.files } } as any);
            }}
            className={`group relative w-full min-h-[180px] border-2 border-dashed rounded-[2rem] flex flex-col items-center justify-center space-y-3 transition-all duration-150 cursor-pointer
              ${isDragging
                ? (isAnyCsvChecked ? 'border-[#4A6CF7] bg-[#4A6CF7]/5' : 'border-red-400 bg-red-50')
                : 'border-gray-200 hover:border-black/30 hover:bg-black/5'
              }`}
          >
            <input type="file" ref={fileInputRef} onChange={handleCsvUpload} className="hidden" accept=".csv,.pdf,.txt,.xlsx,.xls" />
            {isParsing ? (
              <div className="flex flex-col items-center space-y-3">
                <div className="w-10 h-10 border-4 border-[#1A1F36]/30 border-t-[#4A6CF7] rounded-full animate-spin" />
                <p className="font-black uppercase text-[10px] tracking-[0.2em] text-[#45556c]">Analyzing Document...</p>
              </div>
            ) : importSuccess ? (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-white"><Check className="w-6 h-6" /></div>
                <p className="font-black uppercase text-[10px] tracking-[0.2em] text-emerald-600">{importSuccess.count} {importSuccess.type} imported</p>
                {warnings.length > 0 && (
                  <div className="mt-2 px-4 py-2 bg-amber-50 rounded-lg text-left max-w-[280px]">
                    <p className="text-[8px] font-black uppercase tracking-widest text-amber-600 mb-1">Warnings:</p>
                    {warnings.map((w, i) => <p key={i} className="text-[8px] font-medium text-amber-700 leading-tight">• {w}</p>)}
                  </div>
                )}
              </div>
            ) : importError ? (
              <div className="flex flex-col items-center space-y-2 px-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center text-white"><X className="w-6 h-6" /></div>
                <p className="font-black uppercase text-[10px] tracking-[0.2em] text-red-600">{importError}</p>
              </div>
            ) : isDragging ? (
              <p className={`font-black uppercase text-[10px] tracking-[0.2em] text-center px-6 ${isAnyCsvChecked ? 'text-[#4A6CF7]' : 'text-red-500'}`}>
                {isAnyCsvChecked ? 'Drop your file here' : 'Select a data type above before dropping your file'}
              </p>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Upload className="w-5 h-5 text-black" />
                </div>
                <div className="text-center">
                  <p className="font-black uppercase text-[10px] tracking-[0.2em]">Import File (CSV, XLSX, PDF, or TXT)</p>
                  <p className="text-[8px] font-bold uppercase tracking-[0.1em] text-black/30 mt-1">Drag and drop or click to upload</p>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-center pt-1 pb-2">
            <button
              onClick={() => setShowHelpModal(true)}
              className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#1A1F36]/40 hover:text-[#4A6CF7] transition-colors"
            >
              Need help?
            </button>
          </div>
        </div>
      </div>

      {/* Help Modal */}

      <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-4 min-h-[64px]">
        <div className="flex justify-end">
          <button onClick={() => navigate('/settings')} className={backBtnClasses}>
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
        </div>
        <button
          onClick={() => {
            if (isAnyCsvChecked) { setShowCheckboxError(false); fileInputRef.current?.click(); }
            else { setShowCheckboxError(true); }
          }}
          className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}
        >
          {isParsing ? "Analyzing..." : "Upload File"}
        </button>
        <div className="flex justify-start">
          <button onClick={() => setStep('PAST_EARNINGS')} className={backBtnClasses}>
            Skip <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const renderFirstBooking = () => {
    const isImporting = pendingImportedBookings.length > 0;
    const activeBookingId = isImporting ? pendingImportedBookings[0].id : 'new-manual';
    const isFirstBooking = bookedJobs.length === 0;
    const isInvalidClient = currentJob.company === '';
    const isInvalidRate = currentJob.rate === '' || currentJob.rate === '0';
    const isMissingDates = !currentJob.startDate || !currentJob.endDate;

    return (
      <motion.div
        key={activeBookingId}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="flex flex-col h-full"
      >
        <div className="space-y-2">
          <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto">
            <div className="flex items-center justify-start w-full">
              <div className="flex items-center gap-3">
                {isImporting && (
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#4A6CF7]/10 rounded-full text-[9px] font-black uppercase tracking-widest text-[#4A6CF7] whitespace-nowrap">
                    Booking {totalImportedBookings - pendingImportedBookings.length + 1} of {totalImportedBookings}
                  </div>
                )}
              </div>
            </div>
            <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
              {isImporting ? "Confirm" : (returnStep === 'PAST_EARNINGS' ? "Log a" : "Your latest")} <br />
              <span className="text-[#4A6CF7]">Booking.</span>
            </h1>
          </div>

          {importDatesNeedYear && csvImportMode && (
            <div className="w-full max-w-md mx-auto">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">No year found in dates</p>
                  <p className="text-[9px] text-amber-600 leading-tight">What year are these bookings from?</p>
                </div>
                <input
                  type="number"
                  min="2000"
                  max="2035"
                  value={importYearBannerValue}
                  onChange={e => setImportYearBannerValue(e.target.value)}
                  className="w-20 text-center bg-white border border-amber-300 rounded-lg text-sm font-black focus:outline-none focus:border-amber-500 py-1"
                />
                <button
                  onClick={() => handleUpdateImportYear(importYearBannerValue)}
                  className="px-3 py-1.5 bg-amber-500 text-white text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-amber-600 transition-colors flex-shrink-0"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          <div className="w-full max-w-md mx-auto space-y-3">
            <div className="group relative">
              <label className={labelClasses}>Client Name</label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1F36]/30 group-focus-within:text-[#4A6CF7] transition-colors" />
                <input type="text" placeholder="e.g. Google" value={currentJob.company} onChange={e => setCurrentJob({ ...currentJob, company: e.target.value })} className={`${inputClasses} ${isInvalidClient ? 'border-red-300' : ''}`} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="group relative">
                <label className={labelClasses}>Rate Type</label>
                <div className="flex bg-white/60 border border-gray-200 rounded-xl p-1 h-[50px]">
                  <button onClick={() => setCurrentJob({ ...currentJob, rateType: 'daily' })} className={`flex-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${currentJob.rateType === 'daily' ? 'bg-[#4A6CF7] text-white' : 'text-[#45556c] hover:bg-black/5'}`}>Daily</button>
                  <button onClick={() => setCurrentJob({ ...currentJob, rateType: 'hourly' })} className={`flex-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${currentJob.rateType === 'hourly' ? 'bg-[#4A6CF7] text-white' : 'text-[#45556c] hover:bg-black/5'}`}>Hourly</button>
                </div>
              </div>
              <div className="group relative">
                <label className={labelClasses}>Rate</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-sm font-bold text-[#1A1F36]/30 group-focus-within:text-[#4A6CF7] transition-colors pointer-events-none">
                    {currentJob.currency === 'GBP' ? '£' : currentJob.currency === 'EUR' ? '€' : '$'}
                  </div>
                  <input
                    type="number"
                    placeholder="0"
                    value={currentJob.rate}
                    onChange={e => {
                      const val = e.target.value;
                      setCurrentJob({ ...currentJob, rate: val });
                      setManualBilled(null);
                      syncBookingCalculations('rate', { rate: val });
                    }}
                    className={`${inputClasses} ${isInvalidRate ? 'border-red-300' : ''}`}
                  />
                </div>
              </div>
            </div>

            {isMissingDates && importedDays !== null ? (
              <div className="group relative">
                <label className={labelClasses}>What year was this booking?</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1F36]/30 group-focus-within:text-[#4A6CF7] transition-colors" />
                  <input
                    type="number"
                    min="2000"
                    max="2030"
                    placeholder={new Date().getFullYear().toString()}
                    value={bookingYear}
                    onChange={e => {
                      const year = e.target.value;
                      setBookingYear(year);
                      setCurrentJob({ ...currentJob, startDate: `${year}-01-01`, endDate: `${year}-12-31` });
                    }}
                    className={inputClasses}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="group relative">
                  <label className={labelClasses}>Start Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input
                      type="date"
                      value={currentJob.startDate}
                      onChange={e => {
                        const val = e.target.value;
                        const updates: any = { startDate: val };
                        if (!currentJob.endDate || currentJob.endDate < val) updates.endDate = val;
                        setCurrentJob({ ...currentJob, ...updates });
                        setManualBilled(null);
                        syncBookingCalculations('dates', { start: val, end: updates.endDate || currentJob.endDate });
                      }}
                      className={dateInputClasses}
                    />
                  </div>
                </div>
                <div className="group relative">
                  <label className={labelClasses}>End Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input
                      type="date"
                      value={currentJob.endDate}
                      min={currentJob.startDate || undefined}
                      onFocus={e => {
                        if (!e.target.value && currentJob.startDate) {
                          e.target.value = currentJob.startDate;
                          setTimeout(() => { if (!currentJob.endDate) e.target.value = ''; }, 0);
                        }
                      }}
                      onChange={e => {
                        const val = e.target.value;
                        setCurrentJob({ ...currentJob, endDate: val });
                        setManualBilled(null);
                        syncBookingCalculations('dates', { end: val });
                      }}
                      className={dateInputClasses}
                    />
                  </div>
                </div>
              </div>
            )}

            {importedDays !== null && (
              <div className="grid grid-cols-2 gap-4">
                <div className="group relative flex flex-col">
                  <label className={`${labelClasses} min-h-[34px] flex flex-col justify-end pb-0.5`}>
                    Billed Days
                    <span className="block text-[8px] text-black font-bold uppercase tracking-widest mt-0.5">Edit if needed</span>
                  </label>
                  <div className="relative flex-1">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20 group-focus-within:text-[#4A6CF7] transition-colors" />
                    <input
                      type="number"
                      value={importedDays}
                      onChange={e => {
                        const newDays = parseFloat(e.target.value) || 0;
                        setImportedDays(newDays);
                        syncBookingCalculations('days', { days: newDays });
                      }}
                      className={inputClasses}
                    />
                  </div>
                </div>
                <div className="group relative flex flex-col">
                  <label className={`${labelClasses} min-h-[34px] flex flex-col justify-end pb-0.5`}>
                    Total Billed
                    <span className="block text-[8px] invisible mt-0.5">Placeholder</span>
                  </label>
                  <div className="relative flex-1">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-xs font-bold text-black/20 group-focus-within:text-[#4A6CF7] transition-colors pointer-events-none">
                      {currentJob.currency === 'GBP' ? '£' : currentJob.currency === 'EUR' ? '€' : '$'}
                    </div>
                    <input
                      type="text"
                      value={isEarningsFocused ? (importedEarnings ?? '') : (importedEarnings ? new Intl.NumberFormat('en-US').format(importedEarnings) : '')}
                      onFocus={() => setIsEarningsFocused(true)}
                      onBlur={() => setIsEarningsFocused(false)}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        const newEarnings = parseFloat(val) || 0;
                        setImportedEarnings(newEarnings);
                        setHasManuallyEditedBilled(true);
                        syncBookingCalculations('earnings', { earnings: newEarnings });
                      }}
                      className={inputClasses}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="bg-black/5 rounded-2xl p-3 flex flex-col justify-center min-h-[74px]">
              {(!currentJob.startDate || !currentJob.endDate) && importedEarnings === null ? (
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/30 text-center">Enter dates to see your projection</p>
              ) : (
                <div className="flex flex-col items-center space-y-1">
                  <span className="uppercase text-[9px] font-bold tracking-[0.2em] text-[#1A1F36]/50">Projected Billing</span>
                  <div className="text-3xl font-black text-[#4A6CF7]">
                    {formatCurrency(importedEarnings ?? currentJobStats.billed, currentJob.currency)}
                  </div>
                  <div className="text-[10px] font-bold text-[#45556c] uppercase tracking-widest">
                    {importedDays ?? currentJobStats.days} Days
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-3 min-h-[58px]">
          <div className="flex justify-end">
            <button
              onClick={() => {
                setImportSuccess(null);
                setImportError(null);
                setWarnings([]);
                setCsvImportMode(false);
                setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                setImportedDays(null);
                setImportedEarnings(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                setStep('CSV_IMPORT');
              }}
              className={backBtnClasses}
            >
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
          </div>
          <button
            onClick={handleAddBooking}
            disabled={!currentJob.company || !currentJob.rate}
            className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}
          >
            {isImporting ? "Confirm" : "Add Booking"}
          </button>
          <div className="flex justify-start">
            {!isFirstBooking && !isImporting && (
              <button onClick={() => setStep('PAST_EARNINGS')} className={backBtnClasses}>
                Skip <ChevronRight className="w-3 h-3" />
              </button>
            )}
            {isImporting && (
              <button onClick={() => handleBulkConfirm()} className={`${backBtnClasses} whitespace-nowrap`}>
                Confirm All <ChevronRight className="w-3 h-3 ml-1" />
              </button>
            )}
            {isFirstBooking && !isImporting && <div className="w-[40px]" />}
          </div>
        </div>
      </motion.div>
    );
  };

  const renderAddMoreLoop = () => {
    const lastBooking = bookedJobs[bookedJobs.length - 1];
    const latestYearStr = lastBooking?.startDate?.split('-')[0];
    const relevantJobs = latestYearStr ? bookedJobs.filter(j => j.startDate.startsWith(latestYearStr)) : bookedJobs;
    const isBulkAvailable = pendingImportedBookings.length > 1;
    const uniqueCompanies = Array.from(new Set(relevantJobs.map(j => j.company).filter(Boolean)));
    let companyList = uniqueCompanies.join(', ');
    if (companyList.length > 40) companyList = companyList.substring(0, 37) + '...';
    const totalEarningsAggregate = relevantJobs.reduce((sum, j) => sum + (j.earnings || 0), 0);
    const totalDaysAggregate = relevantJobs.reduce((sum, j) => sum + (j.days || 0), 0);
    const bookingsCount = relevantJobs.length;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 1.05 }}
        className="flex flex-col h-full"
      >
        <div className="flex flex-col items-center space-y-4 text-center">
          <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center shadow-xl"><Check className="w-7 h-7 text-white" /></div>
          <h2 className="text-3xl font-black tracking-tight text-[#1A1F36]">
            {latestYearStr && <span className="block text-xl mb-0.5 text-[#4A6CF7]">{latestYearStr}</span>}
            {bookedJobs.length > 1 ? "Bookings" : "Booking"} <br />
            <span className="text-[#4A6CF7]">{bookedJobs.length > 1 ? "Updated." : "Added."}</span>
          </h2>

          {showBulkConfirm ? (
            <div className="bg-black/5 rounded-2xl p-4 space-y-3 w-full max-w-sm">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/50 leading-relaxed">
                You're about to confirm {pendingImportedBookings.length} bookings without reviewing each one. Some may have incomplete data. Are you sure?
              </p>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowBulkConfirm(false)} className="flex-1 py-3 border border-gray-200 rounded-xl font-black uppercase text-[10px] tracking-widest text-[#45556c] hover:bg-black/5 transition-all">Go Back</button>
                <button onClick={handleBulkConfirm} className="flex-1 py-3 bg-[#E3E8F1] text-[#45556c] rounded-full font-black uppercase text-[10px] tracking-widest transition-all hover:bg-black hover:text-white">Confirm All</button>
              </div>
            </div>
          ) : (
            <div className="bg-black/5 rounded-2xl p-5 space-y-4 w-full max-w-sm text-left">
              {bookedJobs.length > 1 ? (
                <>
                  <div className="space-y-1">
                    <label className={labelClasses + " ml-0"}>Clients</label>
                    <div className="font-black text-xl text-[#1A1F36]">{companyList}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>TOTAL BILLED SO FAR</label>
                      <div className="font-black text-2xl text-[#4A6CF7]">{formatCurrency(totalEarningsAggregate, bookedJobs[0]?.currency)}</div>
                    </div>
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>TOTAL DAYS</label>
                      <div className="font-black text-2xl text-[#1A1F36]/50">{totalDaysAggregate} days</div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-black/5">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-[#1A1F36]/50">BOOKINGS CONFIRMED: {bookingsCount}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1">
                    <label className={labelClasses + " ml-0"}>Client Name</label>
                    <div className="font-black text-xl text-[#1A1F36]">{lastBooking?.company}</div>
                  </div>
                  <div className="space-y-1">
                    <label className={labelClasses + " ml-0"}>Date Range</label>
                    <div className="font-black text-base text-[#1A1F36]">{formatDateRange(lastBooking?.startDate, lastBooking?.endDate)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>Total Billed</label>
                      <div className="font-black text-2xl text-[#4A6CF7]">{formatCurrency(lastBooking?.earnings, lastBooking?.currency)}</div>
                    </div>
                    <div className="space-y-1">
                      <label className={labelClasses + " ml-0"}>Duration</label>
                      <div className="font-black text-2xl text-[#1A1F36]/50">{lastBooking?.days} days</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-auto pt-4 space-y-3 w-full max-w-md mx-auto">
          {isBulkAvailable && !showBulkConfirm && (
            <button
              onClick={() => setShowBulkConfirm(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#E3E8F1] rounded-full text-[9px] font-bold uppercase tracking-[0.15em] text-[#45556c] hover:bg-black hover:text-white transition-all mx-auto"
            >
              Confirm All <Check className="w-3 h-3" />
            </button>
          )}
          {!showBulkConfirm && (
            <div className="flex items-center gap-3 w-full">
              <button
                onClick={() => {
                  if (returnStep === 'PAST_EARNINGS') {
                    setStep('FIRST_BOOKING');
                  } else if (csvImportMode) {
                    setImportSuccess(null);
                    setImportError(null);
                    setWarnings([]);
                    setCsvImportMode(false);
                    setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                    setImportedDays(null);
                    setImportedEarnings(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    setStep('CSV_IMPORT');
                  } else {
                    setStep('FIRST_BOOKING');
                  }
                }}
                className={backBtnClasses + " border border-gray-200 justify-center"}
              >
                Add Another <Plus className="w-3 h-3" />
              </button>
              <button onClick={() => setStep('PAST_EARNINGS')} className={primaryBtnClasses + " flex items-center justify-center gap-2"}>
                Continue <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderPastEarnings = () => {
    const histCurrency = bookedJobs[0]?.currency ?? currentJob.currency;
    const histSym = histCurrency === 'GBP' ? '£' : histCurrency === 'EUR' ? '€' : '$';
    return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col h-full"
    >
      <>
        <div className="space-y-1 flex flex-col items-start text-left w-full max-w-md mx-auto mb-4 flex-shrink-0">
          <h1 className="text-4xl font-black tracking-tight text-[#1A1F36] leading-[0.9]">
            Booking <br />
            <span className="text-[#4A6CF7]">History.</span>
          </h1>
        </div>

        <div className="flex-1 min-h-0 flex flex-col max-w-md mx-auto w-full">
          <div className="flex-1 min-h-0 space-y-3 overflow-y-auto pr-2 custom-scrollbar">
              {performanceHistory.map((item, idx) => (
                <React.Fragment key={idx}>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white/60 border border-white rounded-xl p-3 flex items-center gap-4">
                    {confirmArchiveIdx === idx ? (
                      <>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-black uppercase tracking-widest text-red-500">Remove {item.year}?</p>
                          <p className="text-[9px] text-[#1A1F36]/50 leading-tight mt-0.5">This year will be archived from your dashboard.</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => setConfirmArchiveIdx(null)} className="px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-[#1A1F36]/50 hover:text-[#1A1F36] transition-colors">Cancel</button>
                          <button onClick={async () => {
                            const yr = item.year;
                            await Promise.all([
                              supabase.from('bookings').update({ archived: true })
                                .eq('user_id', user!.id)
                                .gte('start_date', `${yr}-01-01`)
                                .lte('start_date', `${yr}-12-31`),
                              supabase.from('yearly_performance').update({ archived: true })
                                .eq('user_id', user!.id)
                                .eq('year', parseInt(yr)),
                              supabase.from('availability_ranges').delete()
                                .eq('user_id', user!.id)
                                .gte('start_date', `${yr}-01-01`)
                                .lte('start_date', `${yr}-12-31`),
                            ]);
                            setConfirmArchiveIdx(null);
                            setPerformanceHistory(prev => prev.filter((_, i) => i !== idx));
                          }} className="px-3 py-1.5 bg-red-500 text-white text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-red-600 transition-colors">Remove</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-20">
                          <label className="block text-[8px] font-bold uppercase tracking-widest text-[#1A1F36]/50 mb-1">Year</label>
                          <input type="text" value={item.year} onChange={e => updateYearData(idx, 'year', e.target.value)} className="w-full bg-transparent font-black text-base focus:outline-none text-[#1A1F36]" />
                        </div>
                        <div className="flex-1">
                          <label className="block text-[8px] font-bold uppercase tracking-widest text-[#1A1F36]/50 mb-1">Total Earned</label>
                          <div className="flex items-center gap-1">
                            <span className="font-black text-[#1A1F36]/30 text-sm">{histSym}</span>
                            <input type="text" value={item.totalEarned === 0 ? '' : new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(item.totalEarned)} onChange={e => updateYearData(idx, 'totalEarned', e.target.value)} placeholder="0" className="w-full bg-transparent font-black text-base focus:outline-none text-[#1A1F36]" />
                          </div>
                        </div>
                        <div className="w-24">
                          <label className="block text-[8px] font-bold uppercase tracking-widest text-[#1A1F36]/50 mb-1">Days worked</label>
                          <input type="number" value={item.totalDaysWorked} onChange={e => updateYearData(idx, 'totalDaysWorked', parseFloat(e.target.value) || 0)} className="w-full bg-transparent font-black text-base focus:outline-none text-[#1A1F36]" />
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleAddDetailedBooking(item.year)} className="p-2 hover:bg-[#4A6CF7]/10 rounded-full transition-colors group" title="Add individual bookings for this year">
                            <PlusCircle className="w-4 h-4 text-[#4A6CF7] opacity-40 group-hover:opacity-100" />
                          </button>
                          <button onClick={() => setConfirmArchiveIdx(idx)} className="p-2 hover:bg-red-50 rounded-full transition-colors group">
                            <X className="w-4 h-4 text-red-300 group-hover:text-red-500" />
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                </React.Fragment>
              ))}

              {performanceHistory.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="py-14 px-8 text-center space-y-6 bg-white/40 border border-white/60 rounded-[2.5rem] shadow-sm w-full"
                >
                  <div className="space-y-2">
                    <p className="text-2xl font-black text-[#1A1F36]">No booking history yet.</p>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/40 max-w-[280px] mx-auto leading-relaxed">
                      Add some to get the most out of FirstHold.
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setShowManualEntryChoice(true)}
                      className="flex-1 py-5 border-2 border-dashed border-[#E3E8F1] rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-[#1A1F36]/20 bg-white/50 transition-all group"
                    >
                      <Plus className="w-5 h-5 text-[#1A1F36]/20 group-hover:text-[#1A1F36] transition-colors" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-[#1A1F36]/40 group-hover:text-[#1A1F36]">Add Manually</span>
                    </button>
                    <button
                      onClick={() => {
                        setReturnStep('PAST_EARNINGS');
                        setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                        setImportSuccess(null);
                        setImportError(null);
                        setWarnings([]);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                        setStep('CSV_IMPORT');
                      }}
                      className="flex-1 py-5 border-2 border-dashed border-[#4A6CF7]/10 rounded-2xl flex flex-col items-center justify-center gap-2 hover:bg-[#4A6CF7]/5 hover:border-[#4A6CF7]/30 bg-white/50 transition-all group"
                    >
                      <Upload className="w-5 h-5 text-[#4A6CF7]/20 group-hover:text-[#4A6CF7] transition-colors" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-[#4A6CF7]/40 group-hover:text-[#4A6CF7]">Import CSV</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {performanceHistory.length > 0 && (
              <div className="pt-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex items-center gap-4 py-3 w-full">
                  <div className="h-[1px] flex-1 bg-[#E3E8F1]" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/30 whitespace-nowrap">Add Bookings</span>
                  <div className="h-[1px] flex-1 bg-[#E3E8F1]" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowManualEntryChoice(true)} className="flex-1 py-4 border-2 border-dashed border-[#E3E8F1] rounded-2xl flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/50 hover:border-gray-200 hover:text-black transition-all">Add Manual Row <Plus className="w-3 h-3" /></button>
                  <button
                    onClick={() => {
                      setReturnStep('PAST_EARNINGS');
                      setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
                      setImportSuccess(null);
                      setImportError(null);
                      setWarnings([]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      setStep('CSV_IMPORT');
                    }}
                    disabled={isParsing}
                    className="flex-1 py-4 border-2 border-dashed border-[#4A6CF7]/10 rounded-2xl flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#4A6CF7]/60 hover:bg-[#4A6CF7]/5 hover:border-[#4A6CF7]/30 transition-all disabled:opacity-50"
                  >
                    {isParsing ? "Analyzing..." : <>Import CSV <Upload className="w-3 h-3" /></>}
                  </button>
                </div>
              </div>
            )}

            {importError && step === 'PAST_EARNINGS' && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-center">
                <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest">{importError}</p>
              </div>
            )}
        </div>

        <div className="max-w-md w-full mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-x-8 mt-auto pt-4 min-h-[64px]">
          <div className="flex justify-end">
            <button onClick={() => {
              setReturnStep(null);
              setCsvContent({ currentYear: false, previousYear: false, multipleYears: false, pastSummary: false });
              setImportSuccess(null);
              setImportError(null);
              setStep('CSV_IMPORT');
            }} className={backBtnClasses}>
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
          </div>

          <button
            onClick={handleSaveAndClose}
            disabled={isSaving}
            className={`${primaryBtnClasses} !flex-none px-8 min-w-[180px]`}
          >
            {isSaving ? 'Saving...' : 'Save & Close'}
          </button>

          <div className="flex justify-start" />
        </div>

        <AnimatePresence>
          {showManualEntryChoice && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-[2rem] p-6 w-full max-w-sm shadow-2xl space-y-5"
              >
                <div className="space-y-1.5">
                  <h3 className="text-xl font-black tracking-tight text-[#1A1F36]">How would you like to add?</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A1F36]/40">Choose an entry method</p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => { handleAddYear(); setShowManualEntryChoice(false); }}
                    className="w-full p-4 rounded-2xl border border-gray-100 bg-white hover:bg-black/5 transition-all text-left flex items-center gap-4 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-sm text-[#1A1F36]">Quick Annual Entry</p>
                      <p className="text-[10px] font-medium text-[#1A1F36]/50 uppercase tracking-widest">Total earnings and days for the year</p>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      const lastYear = performanceHistory.length > 0
                        ? (parseInt(performanceHistory[performanceHistory.length - 1].year) - 1).toString()
                        : (new Date().getFullYear() - 1).toString();
                      handleAddDetailedBooking(lastYear);
                    }}
                    className="w-full p-4 rounded-2xl border border-[#4A6CF7]/20 bg-[#4A6CF7]/5 hover:bg-[#4A6CF7]/10 transition-all text-left flex items-center gap-4 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#4A6CF7] flex items-center justify-center text-white">
                      <FileStack className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-sm text-[#4A6CF7]">Job by Job</p>
                      <p className="text-[10px] font-medium text-[#4A6CF7]/60 uppercase tracking-widest">Every booking, logged individually</p>
                    </div>
                  </button>
                </div>
                <button
                  onClick={() => setShowManualEntryChoice(false)}
                  className="w-full py-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A1F36]/30 hover:text-black transition-colors"
                >
                  Cancel
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </>
    </motion.div>
    );
  };

  // --- Main render ---

  return (
    <div className="min-h-screen w-full bg-[hsl(225,30%,92%)] flex items-center justify-center p-6 font-sans selection:bg-black/10">
      <div className="w-full max-w-[1400px] flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative w-full max-w-2xl bg-white/75 backdrop-blur-[28px] border border-white/65 shadow-xl shadow-blue-900/5 overflow-hidden rounded-[2rem]"
        >
          {/* Encrypted pill */}
          <div className="absolute top-5 right-14 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-500 flex-shrink-0"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
            <span className="text-[9px] font-black uppercase tracking-[0.15em] text-emerald-600">Encrypted</span>
          </div>
          {/* X close button */}
          <button
            onClick={() => navigate('/settings')}
            className="absolute top-4 right-4 z-10 w-7 h-7 rounded-full flex items-center justify-center text-[#1A1F36]/30 hover:text-[#1A1F36] hover:bg-black/5 transition-all"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="p-6 md:p-10 h-[680px] flex flex-col overflow-hidden">
            <AnimatePresence mode="wait">
              {isParsing ? (
                <motion.div key="parsing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col items-center justify-center space-y-6">
                  <div className="w-16 h-16 border-4 border-gray-200 border-t-[#4A6CF7] rounded-full animate-spin" />
                  <div className="text-center space-y-2 px-8">
                    <p className="font-black uppercase text-xs tracking-[0.3em]">Analyzing Booking Data</p>
                    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/50">
                      {isSweating ? (
                        <span className="text-amber-600 animate-pulse">It must be a big file, because Im sweating...</span>
                      ) : isTakingLonger ? (
                        <span className="text-[#4A6CF7] animate-pulse">Its taking longer than usual, give us an extra minute</span>
                      ) : (
                        "This won't take long..."
                      )}
                    </p>
                    <p className="text-[8px] font-black tabular-nums text-[#1A1F36]/20 uppercase tracking-widest mt-1">
                      Walking for {elapsedSeconds} SECONDS
                    </p>
                  </div>
                  <div className="w-full aspect-video rounded-[2.5rem] overflow-hidden shadow-2xl relative bg-black mt-10">
                    <video
                      key={loadingVideos[videoIndex]}
                      src={loadingVideos[videoIndex]}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>
              ) : (
                <>
                  {step === 'CSV_IMPORT' && renderCsvImport()}
                  {step === 'FIRST_BOOKING' && renderFirstBooking()}
                  {step === 'ADD_MORE_LOOP' && renderAddMoreLoop()}
                  {step === 'PAST_EARNINGS' && renderPastEarnings()}
                </>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Logo beneath card */}
        <div className="flex justify-center mt-5">
          <img
            src="https://res.cloudinary.com/dlmbskxuc/image/upload/c_crop,w_175,h_170/v1745797729/logo1h_pmrhki.png"
            alt="Firsthold"
            style={{ width: 34, height: 34, opacity: 0.28, objectFit: 'contain' }}
            draggable={false}
          />
        </div>
      </div>

      {showHelpModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
          onClick={() => setShowHelpModal(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowHelpModal(false)}
              className="absolute top-3.5 right-3.5 w-7 h-7 rounded-full bg-black/6 hover:bg-black/12 flex items-center justify-center transition-colors"
            >
              <X className="w-3.5 h-3.5 text-black/40" />
            </button>

            <div className="px-5 pt-5 pb-2">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4A6CF7]">Import Guide</p>
              <p className="text-base font-black text-[#1A1F36] mt-0.5 leading-tight">Which option should I pick?</p>
            </div>

            <div className="px-5 pb-4 space-y-3 mt-2">
              {[
                { label: "This Year's Bookings", desc: `Individual jobs from ${new Date().getFullYear()}.` },
                { label: "Last Year's Bookings", desc: `Individual jobs from ${new Date().getFullYear() - 1}.` },
                { label: "Other Years", desc: "Individual jobs from any earlier year. Include the year in the filename — e.g. \"2022 Work Track.csv\"." },
                { label: "Annual Sums Only", desc: "Year totals only — no individual jobs. For older years where you only have a summary." },
              ].map(item => (
                <div key={item.label} className="flex gap-3 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#4A6CF7] mt-[5px] flex-shrink-0" />
                  <div>
                    <p className="text-[12px] font-bold text-[#1A1F36]">{item.label}</p>
                    <p className="text-[11px] text-[#1A1F36]/55 leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mx-5 mb-5 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 space-y-2">
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-amber-500">Common issues</p>
              <div className="space-y-2">
                <p className="text-[11px] text-amber-900/75 leading-relaxed"><span className="font-semibold text-amber-900">Missing year in dates (This causes 90% of errors)</span> — Your dates need to include the year. If your spreadsheet has dates like "1/5" or "05/12", use "Other Years" and include the year in your filename — Firsthold picks it up automatically.</p>
                <p className="text-[11px] text-amber-900/75 leading-relaxed"><span className="font-semibold text-amber-900">Junk rows below bookings</span> — Delete anything below your last booking row before uploading.</p>
                <p className="text-[11px] text-amber-900/75 leading-relaxed"><span className="font-semibold text-amber-900">Decimal days</span> — Use 9.5, not 9½.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
